import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, MapPin } from "lucide-react";

export default function BinsDialog({ warehouse, onClose }) {
  const [bins, setBins] = useState(null);
  const [form, setForm] = useState(null); // {code,name,is_default,id?}
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/warehouses/${warehouse.id}/bins`).then((r) => setBins(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.code.trim()) return toast.error("Bin code is required");
    setSaving(true);
    try {
      if (form.id) await api.put(`/bins/${form.id}`, form); else await api.post(`/warehouses/${warehouse.id}/bins`, form);
      toast.success(form.id ? "Bin updated" : "Bin added"); setForm(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete bin ${b.code}?`)) return;
    try { await api.delete(`/bins/${b.id}`); toast.success("Bin deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="bins-dialog">
        <DialogHeader><DialogTitle>Bins · {warehouse.name}</DialogTitle><DialogDescription>Storage locations inside this warehouse.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {!bins ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#6B7189]" /></div> :
            bins.map((b) => (
              <div key={b.id} className="flex items-center justify-between bg-[#F8F9FC] rounded-lg px-3 py-2" data-testid={`bin-row-${b.id}`}>
                <div className="flex items-center gap-2 min-w-0"><MapPin className="w-4 h-4 text-[#6B7189] shrink-0" />
                  <span className="font-mono text-xs bg-white border border-[#E6E8F0] rounded px-1.5 py-0.5">{b.code}</span>
                  <span className="text-sm text-[#101D51] truncate">{b.name}</span>
                  {b.is_default && <span className="text-[10px] text-[#1D4ED8]">default</span>}</div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setForm({ code: b.code, name: b.name, is_default: b.is_default, id: b.id })} className="h-7 w-7 rounded border border-[#E6E8F0] text-[#6B7189] flex items-center justify-center" data-testid={`edit-bin-${b.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  {!b.is_default && <button onClick={() => remove(b)} className="h-7 w-7 rounded border border-[#E6E8F0] flex items-center justify-center" data-testid={`delete-bin-${b.id}`}><Trash2 className="w-3.5 h-3.5 text-[#F83A84]" /></button>}
                </div>
              </div>
            ))}
        </div>
        {form ? (
          <div className="border-t border-[#E6E8F0] pt-3 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Code</Label><Input className="mt-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="A-01" data-testid="bin-code" /></div>
              <div><Label>Name</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aisle A, Rack 1" /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="bin-default" /><span className="text-sm text-[#6B7189]">Default bin</span></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setForm(null)}>Cancel</Button><Button onClick={save} disabled={saving} className="bg-[#101D51]" data-testid="bin-save">{saving ? "Saving…" : "Save bin"}</Button></div>
          </div>
        ) : (
          <DialogFooter><Button onClick={() => setForm({ code: "", name: "", is_default: false })} className="bg-[#0B7C5A] hover:bg-[#09664a]" data-testid="add-bin-btn"><Plus className="w-4 h-4" /> Add bin</Button></DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
